import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  GridFrame,
  GridScroll,
  LoadingState,
  PageHeader,
  StatusChip,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
} from '@tessma/design-system';
import { Refusal, refusalText, formatDay, formatMoney, useAsync } from '../ui/async.js';
import type { FinanceGateway, VatBoxExplanation, VatPeriod, VatReturn } from './gateway.js';

/**
 * VAT PERIODS AND RETURNS.
 *
 * **THERE IS NO EDITABLE BOX ON THIS SCREEN**, and there is no field that
 * could become one. BR-FIN-08 says VAT is derived from transaction data and no
 * user may type a figure onto a return; a form with nine amounts in it would
 * be exactly that, however carefully the server then ignored it. The nine
 * boxes are rendered as text, and every one of them drills through to the
 * transactions behind it.
 *
 * **THE DRILL-THROUGH IS THE PROOF.** A box whose sources do not add up to it
 * is a box somebody typed. The explanation shows the sources and the
 * adjustments the server used; a person adding them up gets the box.
 */

const BOX_LABELS: Readonly<Record<number, string>> = {
  1: 'VAT due on sales',
  2: 'VAT due on acquisitions',
  3: 'Total VAT due',
  4: 'VAT reclaimed on purchases',
  5: 'Net VAT to pay or reclaim',
  6: 'Total sales excluding VAT',
  7: 'Total purchases excluding VAT',
  8: 'Supplies to the EU',
  9: 'Acquisitions from the EU',
};

export function VatPeriodsScreen({
  gateway,
  legalEntityId,
  canPrepare,
  onOpen,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
  readonly canPrepare: boolean;
  readonly onOpen: (vatReturnId: string) => void;
}) {
  const { state, reload } = useAsync<readonly VatPeriod[]>(
    () => gateway.vatPeriods(legalEntityId),
    [gateway, legalEntityId],
  );
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  if (state.status === 'loading') return <LoadingState title="Loading VAT periods…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function prepare(periodId: string) {
    setBusy(true);
    setProblem(undefined);
    try {
      const prepared = await gateway.prepareVatReturn(periodId);
      onOpen(prepared.id);
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The return could not be prepared.'));
    } finally {
      setBusy(false);
    }
  }

  // The shell's `<h1>` reads "VAT". The card underneath said "VAT periods",
  // which is the same page named twice with a word added.
  const header = <PageHeader description="Each period carries the scheme it was opened under." />;

  if (state.value.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon="calendar-blank"
            title="No VAT periods"
            description="A VAT period carries the scheme that was in force while it ran."
          />
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      <Card padding="flush">
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        <GridFrame>
          <GridScroll>
            <Table label="VAT periods">
              <thead>
                <Tr>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th>Due</Th>
                  <Th>Scheme</Th>
                  <Th>Status</Th>
                  {canPrepare ? <Th>Action</Th> : null}
                </Tr>
              </thead>
              <tbody>
                {state.value.map((period) => (
                  <Tr key={period.id}>
                    <Td variant="secondary">{formatDay(period.startsOn)}</Td>
                    <Td variant="secondary">{formatDay(period.endsOn)}</Td>
                    <Td variant="secondary">{formatDay(period.dueOn)}</Td>
                    <Td variant="secondary">{period.scheme.replace(/_/g, ' ')}</Td>
                    <Td>
                      <StatusChip tone={statusTone(period.status)}>{period.status}</StatusChip>
                    </Td>
                    {canPrepare ? (
                      <Td>
                        <Button
                          variant="tertiary"
                          disabled={busy || period.status === 'submitted'}
                          onClick={() => {
                            void prepare(period.id);
                          }}
                        >
                          {period.status === 'submitted' ? 'Filed' : 'Prepare'}
                        </Button>
                      </Td>
                    ) : null}
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </>
  );
}

export function VatReturnScreen({
  gateway,
  vatReturnId,
  canSubmit,
}: {
  readonly gateway: FinanceGateway;
  readonly vatReturnId: string;
  readonly canSubmit: boolean;
}) {
  const { state, reload } = useAsync<VatReturn>(
    () => gateway.vatReturn(vatReturnId),
    [gateway, vatReturnId],
  );
  const [explanation, setExplanation] = useState<VatBoxExplanation | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading the VAT return…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const vatReturn = state.value;

  async function explain(boxNumber: number) {
    setProblem(undefined);
    try {
      setExplanation(await gateway.explainVatBox(vatReturnId, boxNumber));
    } catch (error) {
      setProblem(refusalText(error, 'Could not explain that box.'));
    }
  }

  async function submit() {
    setProblem(undefined);
    try {
      await gateway.submitVatReturn(vatReturnId);
      reload();
    } catch (error) {
      // A step-up refusal arrives here as the server's own 403 with its own
      // words: "This action needs a fresh proof of presence."
      setProblem(refusalText(error, 'The return could not be submitted.'));
    }
  }

  return (
    <div>
      <Card padding="flush">
        <CardHeader
          title="VAT return"
          subtitle={`${vatReturn.status} · derived from the period’s transactions`}
          action={
            canSubmit && vatReturn.status === 'prepared' ? (
              <Button
                variant="primary"
                onClick={() => {
                  void submit();
                }}
              >
                Submit
              </Button>
            ) : undefined
          }
        />
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        <Alert
          tone={vatReturn.reconciledToLedger ? 'success' : 'warning'}
          title={
            vatReturn.reconciledToLedger
              ? 'The return reconciles to the ledger'
              : 'The return does not reconcile to the ledger'
          }
        >
          {vatReturn.reconciledToLedger
            ? 'The net VAT and the movement on the tax control account agree.'
            : `They differ by ${vatReturn.reconciliationDifference ?? 'an unrecorded amount'}. On the
               cash scheme this is expected: the ledger recognised the tax when the document
               posted and the return recognises it when the money moved.`}
        </Alert>
        <GridFrame>
          <GridScroll>
            <Table label="VAT boxes">
              <thead>
                <Tr>
                  <Th>Box</Th>
                  <Th>What it is</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">Sources</Th>
                  <Th>Drill through</Th>
                </Tr>
              </thead>
              <tbody>
                {vatReturn.boxes.map((box) => (
                  <Tr key={box.boxNumber}>
                    <Td>{box.boxNumber}</Td>
                    <Td>{BOX_LABELS[box.boxNumber] ?? ''}</Td>
                    <Td variant="money">{formatMoney(box.amount, 'GBP')}</Td>
                    <Td variant="money">{box.sourceCount}</Td>
                    <Td>
                      <Button
                        variant="tertiary"
                        onClick={() => {
                          void explain(box.boxNumber);
                        }}
                      >
                        Show the transactions
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>

      {explanation !== undefined ? (
        <Card padding="flush">
          <CardHeader
            title={`Box ${String(explanation.boxNumber)} — ${formatMoney(explanation.amount, 'GBP')}`}
            subtitle="The transactions and adjustments the figure was summed from."
          />
          <GridFrame>
            <GridScroll>
              <Table label="Box sources">
                <thead>
                  <Tr>
                    <Th>Kind</Th>
                    <Th>Reference</Th>
                    <Th>Date</Th>
                    <Th align="right">Net</Th>
                    <Th align="right">Tax</Th>
                  </Tr>
                </thead>
                <tbody>
                  {explanation.sources.map((source) => (
                    <Tr key={source.sourceId}>
                      <Td variant="secondary">{source.sourceKind.replace(/_/g, ' ')}</Td>
                      <Td>{source.reference ?? source.sourceId.slice(0, 8)}</Td>
                      <Td variant="secondary">{formatDay(source.occurredOn)}</Td>
                      <Td variant="money">{formatMoney(source.net, 'GBP')}</Td>
                      <Td variant="money">{formatMoney(source.tax, 'GBP')}</Td>
                    </Tr>
                  ))}
                  {explanation.adjustments.map((adjustment) => (
                    <Tr key={adjustment.id}>
                      <Td variant="secondary">adjustment</Td>
                      <Td>{adjustment.reason}</Td>
                      <Td variant="secondary">{adjustment.adjustmentKind.replace(/_/g, ' ')}</Td>
                      <Td variant="money">—</Td>
                      <Td variant="money">{formatMoney(adjustment.amount, 'GBP')}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        </Card>
      ) : null}
    </div>
  );
}
